import { observer } from 'mobx-react';
import { useInjection } from 'inversify-react';
import { useEffect, useState } from 'react';

import { Slider } from '@mui/material';
import { SliderWrapper } from '@/engine/app/modules/images/imageSliderComponent/imageSliderComponent.tsx';
import { TYPES as ImagesTYPES } from '@/engine/app/modules/images/iocTypes.ts';

import type { FC } from 'react';
import { type IImageSliderComponentProps, SliceType } from '@/engine/app/modules/images/interfaces.ts';
import type { ImagesService } from '@/engine/app/modules/images/Images.service.ts';
import type { SlicePositionInfo } from '@/engine/app/modules/images/ImageStackController.ts';

export const ImageRotationComponent: FC<IImageSliderComponentProps> = observer(({ isMaximized, sliceType }) => {
  const imagesService = useInjection<ImagesService>(ImagesTYPES.ImagesService);

  const [rotationValue, setRotationValue] = useState(0);
  const [complimentaryImageSliceToMpr, setComplimentaryImageSliceToMpr] = useState<SliceType>(SliceType.NONE);

  useEffect(() => {
    const _complimentarySliceType = imagesService.getPolylineInfo()?.slice;
    if (!_complimentarySliceType) throw new Error('No centerline selected');
    setComplimentaryImageSliceToMpr(_complimentarySliceType);

    const subscription = imagesService.subscribeToSlicePositionChange(
      sliceType,
      (positionInfo: SlicePositionInfo): void => {
        const value = positionInfo.mprSliceRotations.get(_complimentarySliceType);
        setRotationValue(Math.round(((value ?? 0) * 180) / Math.PI));
      }
    );
    return (): void => subscription.dispose();
  }, [imagesService, sliceType, setRotationValue, setComplimentaryImageSliceToMpr]);

  return (
    <SliderWrapper $isMaximized={isMaximized} className="slider-container">
      <Slider
        sx={{ color: '#01c0b9' }}
        data-testid="imagesRotationSlider"
        orientation="vertical"
        min={-180}
        max={180}
        value={rotationValue}
        onChange={(_, value): void => {
          if (Array.isArray(value)) return;
          imagesService.setImageSliceRotation(sliceType, complimentaryImageSliceToMpr, (value * Math.PI) / 180);
        }}
      />
    </SliderWrapper>
  );
});
